import ApiService from './api';
import { EndpointKeys } from '@/config/api';
import { 
  CountryConfig, 
  ContractorCountryProfile, 
  USOnboardingRequest, 
  USOnboardingResponse,
  CountryCode 
} from '@/types/country';

// Cache for country configurations to avoid repeated API calls
const countryConfigCache = new Map<CountryCode, CountryConfig>();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
let lastCacheUpdate = 0;

export class CountryService {
  /**
   * Get country configuration for a specific country with caching
   */
  static async getConfig(countryCode: CountryCode): Promise<CountryConfig> {
    // Check cache first
    const cached = countryConfigCache.get(countryCode);
    const now = Date.now();
    
    if (cached && (now - lastCacheUpdate) < CACHE_DURATION) {
      return cached;
    }

    try {
      const config = await ApiService.request<CountryConfig>(
        EndpointKeys.GET_COUNTRY_CONFIG,
        {
          urlParams: { countryCode }
        }
      );

      // Cache the result
      countryConfigCache.set(countryCode, config);
      lastCacheUpdate = now;

      return config;
    } catch (error) {
      console.error(`Failed to fetch config for country ${countryCode}:`, error);
      
      // Return cached version if available, even if expired
      if (cached) {
        console.warn(`Using cached config for ${countryCode} due to API error`);
        return cached;
      }
      
      throw new Error(`Unable to fetch configuration for country ${countryCode}. Please try again later.`);
    }
  }

  /**
   * Get all available country configurations
   */
  static async getAllConfigs(): Promise<CountryConfig[]> {
    try {
      // First try to get from the dedicated endpoint
      const response = await ApiService.request<{ countries: Record<string, CountryConfig> }>(
        EndpointKeys.GET_ENABLED_COUNTRIES
      );

      const configs = Object.values(response.countries).filter(config => config.enabled);
      
      if (configs.length > 0) {
        // Cache all configurations
        configs.forEach(config => {
          countryConfigCache.set(config.code as CountryCode, config);
        });
        lastCacheUpdate = Date.now();
        
        return configs;
      }
    } catch (error) {
      console.warn('Failed to fetch countries from dedicated endpoint, falling back to individual requests:', error);
    }

    // Fallback to individual requests if the dedicated endpoint fails
    const supportedCountries: CountryCode[] = ['GB', 'US'];
    
    try {
      // Fetch all configs in parallel
      const configPromises = supportedCountries.map(countryCode => 
        this.getConfig(countryCode).catch(error => {
          console.warn(`Failed to fetch config for ${countryCode}:`, error);
          return null;
        })
      );

      const results = await Promise.all(configPromises);
      
      // Filter out any null results and only return enabled countries
      const configs = results
        .filter((config): config is CountryConfig => config !== null && config.enabled);
      
      if (configs.length === 0) {
        throw new Error('No country configurations available');
      }

      return configs;
    } catch (error) {
      console.error('Failed to fetch country configurations:', error);
      throw new Error('Unable to load country configurations. Please refresh the page.');
    }
  }

  /**
   * Submit US contractor onboarding data
   */
  static async submitUSOnboarding(data: USOnboardingRequest): Promise<USOnboardingResponse> {
    try {
      // Validate required fields
      if (!data.einOrSsn || !data.legalName) {
        throw new Error('EIN/SSN and legal name are required for US onboarding.');
      }

      // Map frontend data format to backend expected format
      const backendData = {
        ein_or_ssn: data.einOrSsn,
        ein_or_ssn_type: data.taxIdType.toUpperCase(), // Convert 'ein' -> 'EIN', 'ssn' -> 'SSN'
        w9_acknowledged: data.w9Acknowledged,
        business_structure: data.businessType === 'individual' ? 'sole_proprietor' : data.businessType,
        legal_name: data.legalName,
        address: {
          line1: data.address.line1,
          line2: data.address.line2,
          city: data.address.city,
          state: data.address.state,
          zip_code: data.address.zipCode,
          country: data.address.country
        }
      };

      const result = await ApiService.request<USOnboardingResponse>(
        EndpointKeys.US_CONTRACTOR_ONBOARD,
        {
          method: 'POST',
          body: backendData
        }
      );

      // Clear cache after successful onboarding as it might affect currency
      this.clearConfigCache();

      return result;
    } catch (error: any) {
      console.error('Failed to submit US onboarding data:', error);
      
      if (error.status === 400) {
        throw new Error(error.message || 'Invalid onboarding data provided.');
      } else if (error.status === 409) {
        throw new Error('This contractor has already completed US onboarding.');
      } else if (error.status === 422) {
        throw new Error(error.message || 'Onboarding validation failed. Please check your information.');
      }
      
      throw new Error('Unable to complete US onboarding. Please try again later.');
    }
  }

  /**
   * Get contractor's country profile
   */
  static async getCountryProfile(contractorId?: string): Promise<ContractorCountryProfile | null> {
    try {
      return await ApiService.request<ContractorCountryProfile>(
        EndpointKeys.GET_COUNTRY_PROFILE
      );
    } catch (error: any) {
      // Handle 404 - profile doesn't exist yet
      if (error.status === 404) {
        return null;
      }
      
      console.error(`Failed to fetch country profile:`, error);
      throw new Error('Unable to fetch contractor profile. Please try again later.');
    }
  }

  /**
   * Update contractor's country profile
   */
  static async updateCountryProfile(
    profileData: Partial<ContractorCountryProfile>
  ): Promise<ContractorCountryProfile> {
    try {
      const result = await ApiService.request<ContractorCountryProfile>(
        EndpointKeys.UPDATE_COUNTRY_PROFILE,
        {
          method: 'PUT',
          body: profileData
        }
      );

      // Clear any cached configs since profile update might affect currency
      this.clearConfigCache();
      
      return result;
    } catch (error: any) {
      console.error(`Failed to update country profile:`, error);
      
      if (error.status === 400) {
        throw new Error(error.message || 'Invalid profile data provided.');
      } else if (error.status === 404) {
        throw new Error('Contractor profile not found.');
      } else if (error.status === 422) {
        throw new Error(error.message || 'Profile validation failed.');
      }
      
      throw new Error('Unable to update contractor profile. Please try again later.');
    }
  }

  /**
   * Clear the country configuration cache
   */
  static clearConfigCache(): void {
    countryConfigCache.clear();
    lastCacheUpdate = 0;
  }

  /**
   * Utility method to validate if a country code is supported
   */
  static isSupportedCountry(countryCode: string): countryCode is CountryCode {
    const supportedCountries = ['GB', 'US'];
    return supportedCountries.includes(countryCode);
  }

  /**
   * Get currency information for a specific country
   */
  static async getCurrencyInfo(countryCode: CountryCode): Promise<{ code: string; symbol: string }> {
    try {
      const config = await this.getConfig(countryCode);
      return {
        code: config.currencyCode,
        symbol: config.currencySymbol
      };
    } catch (error) {
      console.warn(`Failed to get currency info for ${countryCode}, using defaults`);
      // Return sensible defaults
      return countryCode === 'US' 
        ? { code: 'USD', symbol: '$' }
        : { code: 'GBP', symbol: '£' };
    }
  }

  /**
   * Format currency amount for a specific country
   */
  static async formatCurrency(amount: number, countryCode: CountryCode): Promise<string> {
    try {
      const { code, symbol } = await this.getCurrencyInfo(countryCode);
      
      // Use Intl.NumberFormat for proper formatting
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: code,
      }).format(amount);
    } catch (error) {
      // Fallback formatting
      const symbol = countryCode === 'US' ? '$' : '£';
      return `${symbol}${amount.toFixed(2)}`;
    }
  }

  /**
   * Get supported country codes
   */
  static getSupportedCountryCodes(): CountryCode[] {
    return ['GB', 'US'];
  }

  /**
   * Get country name by code
   */
  static async getCountryName(countryCode: CountryCode): Promise<string> {
    try {
      const config = await this.getConfig(countryCode);
      return config.name;
    } catch (error) {
      // Return fallback names
      return countryCode === 'US' ? 'United States' : 'United Kingdom';
    }
  }

  /**
   * Get country code based on user's IP address
   * This calls the backend endpoint to avoid CORS issues
   */
  static async getCountryFromIP(): Promise<CountryCode | null> {
    try {
      const response = await ApiService.request<{ country_code: CountryCode | null; success: boolean }>(
        EndpointKeys.GET_COUNTRY_FROM_IP
      );

      if (response.success && response.country_code) {
        return response.country_code;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get country from IP:', error);
      return null;
    }
  }
}

export default CountryService; 